import * as THREE from "../../../libs/three.js/build/three.module.js";

import {t} from "../../digitization/i18n/index.js";
// Vertex/edge snapping for picks on IFC/OBJ-style meshes.
//
// A plain raycast hit lands wherever the cursor happens to be on a face,
// which is useless for measuring to a corner or an edge ("dette hjørne",
// "denne kant"). This nudges a hit onto the nearest *feature* of the mesh
// when one is close enough on screen, with a clear preference for vertices
// over edges and edges over open face.
//
// Only real feature edges are snapped to - the mesh's own userData.edges
// overlay (an EdgesGeometry, i.e. edges where adjacent faces actually
// meet at an angle). Snapping to the raw triangulation would put the cursor
// on diagonals that do not exist in the model.
//
// It is hooked into Utils.getMousePointCloudIntersection, which every
// measuring tool already funnels through, so existing tools (distance,
// height, angle, ...) get the behaviour without any change of their own.

export const MeshSnapping = {
	enabled: true,
	vertexPx: 14, // screen radius within which a vertex wins
	edgePx: 9, // screen radius within which an edge wins (vertices take precedence)
	maxEdgeVertices: 400000, // a mesh with more feature-edge vertices than this is not snapped (keeps hover cheap)
};

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _ab = new THREE.Vector3();
const _w = new THREE.Vector3();
const _p = new THREE.Vector3();
const _q = new THREE.Vector3();
const _proj = new THREE.Vector3();

function worldPerPixel(camera, renderer, distance){
	let height = renderer.domElement.clientHeight || 1;
	if(camera.isOrthographicCamera){
		return (camera.top - camera.bottom) / camera.zoom / height;
	}
	return 2 * distance * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) / height;
}

function toPixels(point, camera, renderer, out){
	_proj.copy(point).project(camera);
	let w = renderer.domElement.clientWidth;
	let h = renderer.domElement.clientHeight;
	out.x = (_proj.x * 0.5 + 0.5) * w;
	out.y = (1 - (_proj.y * 0.5 + 0.5)) * h;
	return _proj.z >= -1 && _proj.z <= 1;
}

function distanceToSegment(p, a, b){
	_ab.subVectors(b, a);
	let len2 = _ab.lengthSq();
	let t = len2 < 1e-12 ? 0 : Math.max(0, Math.min(1, _w.subVectors(p, a).dot(_ab) / len2));
	return _q.copy(a).addScaledVector(_ab, t).distanceTo(p);
}

// Closest point on segment ab to the (infinite) camera ray - gives the
// perspective-correct point on the edge under the cursor.
function closestPointOnSegmentToRay(ray, a, b, out){
	let d1 = ray.direction;
	let d2 = _ab.subVectors(b, a);
	let r = _w.subVectors(ray.origin, a);
	let aa = d1.dot(d1);
	let e = d2.dot(d2);
	let f = d2.dot(r);
	let c = d1.dot(r);
	let bb = d1.dot(d2);
	let denom = aa * e - bb * bb;
	let t = Math.abs(denom) < 1e-12 ? 0 : (aa * f - bb * c) / denom;
	t = Math.max(0, Math.min(1, t));
	return out.copy(a).addScaledVector(d2, t);
}

// Snapping is a measuring aid, not a navigation behaviour: it is only on
// while a measuring/drawing tool is actually in use - a Potree measuring
// tool mid-insertion (or dragging a marker), a Digitalisering tool, or the
// PrecisionMeasure tools. Otherwise ordinary picks (orbit pivot, plain
// hover/click) land exactly where the cursor is.
export function isMeasuringToolActive(viewer){
	if(!viewer){
		return false;
	}
	let input = viewer.inputHandler;
	if(input && input.drag && input.drag.object){
		return true;
	}
	let dm = viewer.digitizationModule;
	return !!(dm && dm.activeTool);
}

let indicatorEl = null;
let indicatorLabelEl = null;
let indicatorHideTimer = null;

function ensureIndicator(renderer){
	let parent = renderer.domElement.parentElement;
	if(!parent){
		return false;
	}
	if(indicatorEl && indicatorEl.parentElement === parent){
		return true;
	}
	if(indicatorEl){
		indicatorEl.remove();
	}
	indicatorEl = document.createElement("div");
	indicatorEl.style.cssText = "position:absolute; z-index:898; pointer-events:none; display:none; width:14px; height:14px; margin:-7px 0 0 -7px; box-sizing:border-box; border:2px solid #ff9800; background:rgba(255,152,0,0.18); box-shadow:0 0 0 1px rgba(0,0,0,0.55);";
	indicatorLabelEl = document.createElement("div");
	indicatorLabelEl.style.cssText = "position:absolute; left:14px; top:-6px; background:rgba(0,0,0,0.75); color:#fff; padding:0 5px; border-radius:3px; font:10px sans-serif; white-space:nowrap; transform:none;";
	indicatorEl.appendChild(indicatorLabelEl);
	parent.appendChild(indicatorEl);
	return true;
}

export function hideSnapIndicator(){
	if(indicatorEl){
		indicatorEl.style.display = "none";
	}
}

// persist: the caller owns hiding it (a tool with its own live preview);
// otherwise it fades out shortly after the last pick that showed it.
export function showSnapIndicator(renderer, camera, point, type, persist = false){
	if(!ensureIndicator(renderer)){
		return;
	}
	let px = {x: 0, y: 0};
	if(!toPixels(point, camera, renderer, px)){
		hideSnapIndicator();
		return;
	}
	let canvas = renderer.domElement;
	indicatorEl.style.left = `${canvas.offsetLeft + px.x}px`;
	indicatorEl.style.top = `${canvas.offsetTop + px.y}px`;
	if(type === "vertex"){
		indicatorEl.style.borderColor = "#ff9800";
		indicatorEl.style.background = "rgba(255,152,0,0.18)";
		indicatorEl.style.transform = "none";
		indicatorLabelEl.textContent = t("Hjørne");
	}else{
		indicatorEl.style.borderColor = "#26c6da";
		indicatorEl.style.background = "rgba(38,198,218,0.18)";
		indicatorEl.style.transform = "rotate(45deg)";
		indicatorLabelEl.textContent = t("Kant");
	}
	// counter-rotate the label so it stays horizontal on the diamond
	indicatorLabelEl.style.transform = type === "vertex" ? "none" : "rotate(-45deg)";
	indicatorEl.style.display = "block";

	// The indicator belongs to the *last pick*; if no tool keeps picking
	// (mouse idle/outside, tool ended) it must not linger.
	clearTimeout(indicatorHideTimer);
	if(!persist){
		indicatorHideTimer = setTimeout(hideSnapIndicator, 1500);
	}
}

// hit: a THREE.Raycaster intersection on a mesh; ray: the picking ray;
// mouse: {x, y} in canvas pixels. Returns {point, type: "vertex"|"edge"}
// or null if nothing is close enough (or the mesh has no feature edges).
export function snapMeshHit(hit, ray, mouse, camera, renderer){
	if(!MeshSnapping.enabled || !hit || !hit.object){
		return null;
	}
	let edges = hit.object.userData && hit.object.userData.edges;
	let posAttr = edges && edges.geometry && edges.geometry.attributes.position;
	if(!posAttr || posAttr.count < 2 || posAttr.count > MeshSnapping.maxEdgeVertices){
		return null;
	}

	let matrix = edges.matrixWorld;
	let wpp = worldPerPixel(camera, renderer, hit.distance);
	let reach = Math.max(MeshSnapping.vertexPx, MeshSnapping.edgePx) * wpp * 1.5;

	let bestVertex = null;
	let bestEdge = null;
	let pa = {x: 0, y: 0};
	let pb = {x: 0, y: 0};

	for(let i = 0; i + 1 < posAttr.count; i += 2){
		_a.fromBufferAttribute(posAttr, i).applyMatrix4(matrix);
		_b.fromBufferAttribute(posAttr, i + 1).applyMatrix4(matrix);

		// cheap 3D prefilter around the face hit - only features that
		// actually lie near the visible surface under the cursor
		if(distanceToSegment(hit.point, _a, _b) > reach){
			continue;
		}

		let aVisible = toPixels(_a, camera, renderer, pa);
		let bVisible = toPixels(_b, camera, renderer, pb);

		if(aVisible){
			let d = Math.hypot(pa.x - mouse.x, pa.y - mouse.y);
			if(d <= MeshSnapping.vertexPx && (!bestVertex || d < bestVertex.px)){
				bestVertex = {px: d, point: _a.clone()};
			}
		}
		if(bVisible){
			let d = Math.hypot(pb.x - mouse.x, pb.y - mouse.y);
			if(d <= MeshSnapping.vertexPx && (!bestVertex || d < bestVertex.px)){
				bestVertex = {px: d, point: _b.clone()};
			}
		}

		if(aVisible || bVisible){
			closestPointOnSegmentToRay(ray, _a, _b, _p);
			let pp = {x: 0, y: 0};
			if(toPixels(_p, camera, renderer, pp)){
				let d = Math.hypot(pp.x - mouse.x, pp.y - mouse.y);
				if(d <= MeshSnapping.edgePx && (!bestEdge || d < bestEdge.px)){
					bestEdge = {px: d, point: _p.clone()};
				}
			}
		}
	}

	if(bestVertex){
		return {point: bestVertex.point, type: "vertex"};
	}
	if(bestEdge){
		return {point: bestEdge.point, type: "edge"};
	}
	return null;
}
